// src/ConsumerApp/Screens/ProductDetails/components/ProductOptionCard.js

import React, { useState, useEffect } from 'react';
import WhitePlus from '../../../../assets/icons/WhitePlus.svg';
import WhiteMinus from '../../../../assets/icons/WhiteMinus.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

import {
  useAddItemToBucket,
  useUpdateBucketItemQuantity,
  useDeleteBucketItem,
} from '../../../api/products/product.hooks';
import { guestBucketManager } from '../../../utils/guestBucketManager';

const IMAGE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function ProductOptionCard({
  item,
  customerId,
  hubId,
  purchaseType = 'BUY_NOW',
  bucketId,
  savedBucketQuantity,
  onAddSuccess,
}) {
  const productId = String(item.product_id || item.id || item._id);
  const name = item.product_name || item.title || item.name || 'Material Option';
  const uom = item.uom || item.uom_name || item.UOM || 'Unit';
  const price = Number(item.selling_price || item.buy_now?.unit_price || item.price || 0);
  const mrp = Number(item.mrp || 0);
  const minQty = Number(item.buy_now?.min_qty || item.min_qty || 1);
  const maxQty = Number(item.buy_now?.max_qty || 999);

  const rawImg = item.product_image_url || item.product_image || item.image;
  const fullImg = rawImg
    ? rawImg.startsWith('http')
      ? rawImg
      : `${IMAGE_URL}${rawImg}`
    : null;

  const [imgError, setImgError] = useState(false);
  const [isAdded, setIsAdded] = useState(savedBucketQuantity !== undefined && savedBucketQuantity > 0);
  const [inputValue, setInputValue] = useState(
    savedBucketQuantity !== undefined && savedBucketQuantity > 0
      ? String(savedBucketQuantity)
      : String(minQty)
  );

  const addItemMutation = useAddItemToBucket();
  const updateItemMutation = useUpdateBucketItemQuantity();
  const deleteItemMutation = useDeleteBucketItem();

  useEffect(() => {
    if (savedBucketQuantity !== undefined && savedBucketQuantity > 0) {
      setIsAdded(true);
      setInputValue(String(savedBucketQuantity));
    } else {
      setIsAdded(false);
      setInputValue(String(minQty));
    }
  }, [savedBucketQuantity, minQty]);

  const handleInitialAdd = (e) => {
    e.stopPropagation();
    const initialQty = parseInt(inputValue, 10) || minQty;
    setIsAdded(true);
    setInputValue(String(initialQty));

    if (!customerId) {
      guestBucketManager.addItem(item, initialQty);
      if (onAddSuccess) onAddSuccess(item);
    } else {
      addItemMutation.mutate(
        {
          customer_id: customerId,
          hubId,
          item_id: productId,
          purchase_type: purchaseType,
          quantity: initialQty,
        },
        {
          onSuccess: () => {
            if (onAddSuccess) onAddSuccess(item);
          },
        }
      );
    }
  };

  const handleIncrement = (e) => {
    e.stopPropagation();
    const next = (parseInt(inputValue, 10) || 0) + 1;
    if (next <= maxQty) {
      setInputValue(String(next));

      if (!customerId) {
        guestBucketManager.updateQuantity(productId, next);
      } else {
        updateItemMutation.mutate({
          customer_id: customerId,
          hubId,
          item_id: productId,
          purchase_type: purchaseType,
          quantity: next,
        });
      }
    }
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    const next = (parseInt(inputValue, 10) || 0) - 1;

    if (next < minQty) {
      setIsAdded(false);
      setInputValue(String(minQty));

      if (!customerId) {
        guestBucketManager.removeItem(productId);
      } else {
        deleteItemMutation.mutate(
          { customer_id: customerId, item_id: productId, bucket_id: bucketId }
        );
      }
    } else {
      setInputValue(String(next));

      if (!customerId) {
        guestBucketManager.updateQuantity(productId, next);
      } else {
        updateItemMutation.mutate({
          customer_id: customerId,
          hubId,
          item_id: productId,
          purchase_type: purchaseType,
          quantity: next,
        });
      }
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.imageBox}>
        {fullImg && !imgError ? (
          <img
            src={fullImg}
            alt={name}
            style={styles.img}
            onError={() => setImgError(true)}
          />
        ) : (
          <img src={ToteIcon} alt="fallback" style={styles.toteFallback} />
        )}
      </div>

      <div style={styles.infoCol}>
        <h5 style={styles.title} title={name}>
          {name}
        </h5>
        <span style={styles.unitText}>Unit: {uom}</span>
        <div style={styles.priceRow}>
          <span style={styles.priceText}>₹{price.toLocaleString('en-IN')}</span>
          {mrp > price && (
            <span style={styles.mrpText}>₹{mrp.toLocaleString('en-IN')}</span>
          )}
        </div>
      </div>

      <div style={styles.actionCol} onClick={(e) => e.stopPropagation()}>
        {!isAdded ? (
          <button
            type="button"
            style={styles.addBtn}
            onClick={handleInitialAdd}
            disabled={addItemMutation.isLoading}
          >
            {addItemMutation.isLoading ? '...' : 'ADD'}
          </button>
        ) : (
          <div style={styles.zeptoStepper}>
            <button
              type="button"
              style={styles.stepperBtn}
              onClick={handleDecrement}
              aria-label="Decrease quantity"
            >
              <img src={WhiteMinus} alt="-" style={{ width: 14, height: 14 }} />
            </button>
            <span style={styles.stepperCount}>{inputValue}</span>
            <button
              type="button"
              style={styles.stepperBtn}
              onClick={handleIncrement}
              aria-label="Increase quantity"
            >
              <img src={WhitePlus} alt="+" style={{ width: 14, height: 14 }} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 10,
    boxSizing: 'border-box',
    width: '100%',
  },
  imageBox: {
    width: 52,
    height: 52,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  img: {
    maxWidth: '85%',
    maxHeight: '85%',
    objectFit: 'contain',
  },
  toteFallback: {
    width: 24,
    height: 24,
    opacity: 0.35,
  },
  infoCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: 13,
    fontWeight: '700',
    color: '#2B2A29',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  unitText: {
    fontSize: 11,
    color: '#707B8C',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginTop: 2,
  },
  priceText: {
    fontSize: 13.5,
    fontWeight: '800',
    color: '#2B2A29',
  },
  mrpText: {
    fontSize: 11,
    color: '#707B8C',
    textDecoration: 'line-through',
  },
  actionCol: {
    flexShrink: 0,
  },
  addBtn: {
    backgroundColor: '#FFFFFF',
    color: '#E9001E',
    border: '1px solid #E9001E',
    borderRadius: 6,
    padding: '6px 16px',
    fontSize: 12,
    fontWeight: '800',
    cursor: 'pointer',
    letterSpacing: '0.4px',
    boxShadow: '0 1px 3px rgba(233,0,30,0.12)',
  },
  zeptoStepper: {
    backgroundColor: '#E9001E',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 4px',
    width: 84,
    height: 30,
    boxSizing: 'border-box',
  },
  stepperBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    width: 22,
    height: '100%',
  },
  stepperCount: {
    fontSize: 13,
    fontWeight: '800',
    color: '#FFFFFF',
    userSelect: 'none',
  },
};